import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';

export type ConfirmRevokeModalProps = {
  count: Nullable<number>;
  integrationId: string;
  selectedUsers?: string[];
  onConfirmRevoke: () => void;
  onHide: () => void;
};

export const ConfirmRevokeModal: FC<ConfirmRevokeModalProps> = ({
  count = 0,
  integrationId = '',
  selectedUsers = [],
  onConfirmRevoke = () => {},
  onHide = () => {},
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { toast: errorToast } = useErrorToast();
  const [revokeIntegrationUsers, { isLoading }] =
    api.useRevokeIntegrationUsersMutation();

  const onConfirmClick = async () => {
    if (!integrationId || selectedUsers.length === 0) return;
    try {
      onConfirmRevoke();
      await revokeIntegrationUsers({
        integrationId,
        token_ids: selectedUsers,
      }).unwrap();
      toast({
        message: intl.formatMessage(
          {
            id: 'NewESignature.RevokeToast.Message',
            defaultMessage:
              '{count, plural, one {# account was revoked.} other {# accounts were revoked.}}',
            description:
              'Toast message shown after revoking user access. Example: 1 account was revoked, 2 accounts were revoked.',
          },
          { count },
        ),
        status: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      errorToast(error instanceof Error ? error : new Error(String(error)));
    }
  };
  return (
    <Modal
      isVisible={true}
      onHide={onHide}
      title={intl.formatMessage(
        {
          id: 'NewESignature.RevokeModal.Title',
          defaultMessage:
            'Revoke Access for {count, plural, one {# User} other {# Users}}',
          description: 'Modal title for revoking integration user access',
        },
        { count },
      )}
      primaryAction={{
        variant: 'danger',
        text: intl.formatMessage({
          id: 'NewESignature.SidePanel.Revoke',
          defaultMessage: 'Revoke',
          description: 'Revoke button',
        }),
        onClick: onConfirmClick,
        isLoading,
      }}
    >
      {intl.formatMessage({
        id: 'NewESignature.RevokeModal.Message',
        defaultMessage:
          'Authenticated users will lose access to the e-signature integration. Documents that have been initiated by this user for e-signature but have not been fully signed will need to restart the signature process.',
        description: 'Modal message for revoking integration user access',
      })}
    </Modal>
  );
};
